package ui.console

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.*
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.Check
import compose.assets.svg.Check_all
import compose.assets.svg.Clock_outline
import compose.assets.svg.DeleteEmpty
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.device_detail__actions__remote_console
import tk.mallumo.compose.navigation.ComposableNavNode
import tools.rememberString
import ui.widget.*

data class ConsoleArgs(var mac: String = "")

@Composable
@ComposableNavNode(ConsoleArgs::class)
fun ConsoleUI() {
    with(ConsoleScope.remember()) {
        Content()
    }
}

@Composable
fun ConsoleScope.Content() {
    val state = rememberLazyListState()
    val historyDialog = DialogState.remember(key = "cmd-history")

    val cmd = remember {
        mutableStateOf(TextFieldValue("pwd"))
    }

    DialogCmdHistory(historyDialog, cmd)

    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = Res.string.device_detail__actions__remote_console.rememberString(),
            titleSuffix = "(${mac})"
        )
        Text(
            text = vm.connectionState,
            modifier = Modifier.fillMaxWidth()
                .background(MaterialTheme.colorScheme.surface)
                .padding(8.dp)
        )
        LazyColumn(
            state = state,
            verticalArrangement = Arrangement.spacedBy(16.dp),
            contentPadding = PaddingValues(8.dp),
            reverseLayout = true,
            modifier = Modifier.fillMaxWidth()
                .weight(1F)
        ) {
            items(vm.messages) {
                Item(it)
            }
        }
        CommandInput(cmd, state, historyDialog)
    }

    DisposableEffect(Unit) {
        vm.connect()
        onDispose {
            vm.disconnect()
        }
    }
}

@Composable
private fun ConsoleScope.DialogCmdHistory(state: DialogState, cmd: MutableState<TextFieldValue>) {
    val history = vm.history.collectAsState()

    @Composable
    fun EntryUi(string: String) {
        Card({
            cmd.value = TextFieldValue(string.trim())
            state.hide()
        }) {
            Text(string, Modifier.padding(16.dp))
        }
    }

    Dialog(
        state = state,
        onDismiss = { state.hide() }) {
        DialogUI(
            modifier = Modifier.fillMaxSize(),
            onDismissRequest = { state.hide() },
        ) {
            LazyColumn(
                Modifier.fillMaxSize(),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                item {
                    EntryUi("sudo journalctl --since \"1 day ago\" -u device-runtime.service > /tmp/ff && cat /tmp/ff")
                }
                items(history.value) {
                    EntryUi(it)
                }
            }
        }
    }
}

@Composable
fun ConsoleScope.Item(holder: ConsoleVM.MsgHolder) {

    Card(Modifier.fillMaxWidth()) {
        Column {
            Text(
                text = holder.dt.time.toString(),
                style = MaterialTheme.typography.labelSmall,
                modifier = Modifier
                    .padding(8.dp)
            )
            SelectionContainer {
                Text(
                    text = holder.send.command,
                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                    modifier = Modifier.fillMaxSize()
                        .background(MaterialTheme.colorScheme.primaryContainer)
                        .padding(8.dp)
                )
            }
            HorizontalDivider()
            if (holder.receive == null) {
                when (holder.sendOk) {
                    null -> Text("SENDING", Modifier.padding(8.dp))
                    true -> Text("SEND OK", Modifier.padding(8.dp))
                    false -> Text("SEND ERR", Modifier.padding(8.dp))
                }
            } else {
                SelectionContainer {
                    Column {
                        Text(
                            text = holder.message.takeIf { it.isNotEmpty() } ?: "OK",
                            color = MaterialTheme.colorScheme.onSecondaryContainer,
                            modifier = Modifier.fillMaxSize()
                                .background(MaterialTheme.colorScheme.secondaryContainer)
                                .padding(8.dp)
                        )
                        HorizontalDivider()
                        if (!holder.receive?.statusStr.isNullOrEmpty()) {
                            Text(
                                text = "ERR: ${holder.receive?.statusInt}\n${holder.receive?.statusStr}",
                                color = MaterialTheme.colorScheme.onErrorContainer,
                                modifier = Modifier.fillMaxSize()
                                    .background(MaterialTheme.colorScheme.errorContainer)
                                    .padding(8.dp)
                            )
                        }
                    }
                }
            }
        }
    }
}


@Composable
fun ConsoleScope.CommandInput(cmd: MutableState<TextFieldValue>, state: LazyListState, historyDialog: DialogState) {
    val scope = rememberCoroutineScope()

    val focus = remember {
        FocusRequester()
    }

    fun invokeCommand() {
        action(ConsoleVM.Action.Commit(cmd.value.text))
        scope.launch {
            delay(100)
            state.scrollToItem(vm.messages.lastIndex)
        }
    }
    TextField(
        value = cmd.value,
        onValueChange = {
            cmd.value = it
        },
        prefix = {
            IconButton({
                cmd.value = TextFieldValue()
                focus.requestFocus()
            }) {
                Icon(Svg.DeleteEmpty, null)
            }
        },
        suffix = {
            Row {
                IconButton({
                    invokeCommand()
                }) {
                    Icon(Svg.Check, null)
                }
                Space(8.dp)
                IconButton({
                    focus.requestFocus()
                    invokeCommand()
                    cmd.value = TextFieldValue()
                }) {
                    Icon(Svg.Check_all, null)
                }
                IconButton({
                    historyDialog.show()
                }) {
                    Icon(Svg.Clock_outline, null)
                }
            }
        },
        modifier = Modifier.fillMaxWidth()
            .focusRequester(focus)
            .onKeyEvent {
                if (it.type == KeyEventType.KeyDown && it.isCtrlPressed && it.key == Key.Enter) {
                    invokeCommand()
                    true
                } else false
            }
    )
}
